import { useMemo } from 'react';

import { q } from '@actual-app/core/shared/query';
import type { IntegerAmount } from '@actual-app/core/shared/util';
import { useQuery } from '@tanstack/react-query';

import type {
  Categorias,
  Movimiento,
} from '#components/mobile/informes/calculos';
import {
  condicionNoIngreso,
  descontarNoIngresos,
} from '#components/mobile/informes/noIngreso';
import { useCategoriasExcluidas } from '#components/mobile/informes/useCategoriasExcluidas';
import { useCategoriasInfo } from '#components/mobile/informes/useTotalesMensuales';
import { useSyncedPref } from '#hooks/useSyncedPref';
import { aqlQuery } from '#queries/aqlQuery';

import type { DiaCorte, MovimientoDia } from './diario';

const CADUCIDAD_MS = 30_000;

/** Los mismos filtros que los demás informes: cuentas del presupuesto y sin traspasos. */
const SIN_TRASPASOS = {
  'account.offbudget': false,
  $or: [
    { 'payee.transfer_acct': null },
    { 'payee.transfer_acct.offbudget': true },
  ],
};

function consultaDias(desde: string, hasta: string, soloNoIngreso: boolean) {
  let consulta = q('transactions').filter({
    // Dos condiciones aparte: AQL solo aplica el primer operador de cada objeto.
    $and: [{ date: { $gte: desde } }, { date: { $lte: hasta } }],
    ...SIN_TRASPASOS,
  });
  if (soloNoIngreso) {
    consulta = consulta.filter(condicionNoIngreso);
  }
  return consulta
    .groupBy(['date', { $id: '$category' }, { $id: '$payee' }])
    .select([
      'date',
      { category: { $id: '$category.id' } },
      { payee: { $id: '$payee.id' } },
      { amount: { $sum: '$amount' } },
    ]);
}

type Fila = {
  date: string;
  category: string | null;
  payee: string | null;
  amount: IntegerAmount;
};

function aMovimientos(filas: Fila[]): Movimiento[] {
  // `mes` lleva la fecha: así `descontarNoIngresos` cuadra por día.
  return filas.map(f => ({
    mes: f.date,
    categoria: f.category,
    payee: f.payee,
    importe: f.amount,
  }));
}

/**
 * Movimientos entre dos fechas agrupados por día × categoría ×
 * beneficiario, sin lo que no cuenta como ingreso y sin las categorías
 * ocultas ni las que la persona quitó de los informes.
 */
export function useMovimientosDiarios(
  desde: string,
  hasta: string,
): {
  movimientos: MovimientoDia[];
  categorias: Categorias;
  isLoading: boolean;
} {
  const { data, isLoading } = useQuery({
    queryKey: ['informes', 'diario', desde, hasta],
    queryFn: async () => {
      const [{ data: filas }, { data: excluidos }] = await Promise.all([
        aqlQuery(consultaDias(desde, hasta, false)),
        aqlQuery(consultaDias(desde, hasta, true)),
      ]);
      return {
        movimientos: aMovimientos(filas as Fila[]),
        excluidos: aMovimientos(excluidos as Fila[]),
      };
    },
    staleTime: CADUCIDAD_MS,
  });
  const categorias = useCategoriasInfo();
  const { excluidas } = useCategoriasExcluidas();

  const movimientos = useMemo(() => {
    if (!data) {
      return [];
    }
    return descontarNoIngresos(data.movimientos, data.excluidos, categorias)
      .filter(m => {
        if (m.categoria == null) {
          return true;
        }
        if (excluidas.has(m.categoria)) {
          return false;
        }
        return !categorias.get(m.categoria)?.oculta;
      })
      .map<MovimientoDia>(m => ({
        fecha: m.mes,
        categoria: m.categoria,
        payee: m.payee,
        importe: m.importe,
      }));
  }, [data, categorias, excluidas]);

  return { movimientos, categorias, isLoading };
}

export type GastoGrande = {
  id: string;
  date: string;
  amount: IntegerAmount;
  payee: string | null;
  category: string | null;
  notes: string | null;
};

/** Las salidas más grandes del periodo (sin traspasos). */
export function useGastosGrandes(
  desde: string,
  hasta: string,
  cuantos = 3,
): GastoGrande[] {
  const { data } = useQuery({
    queryKey: ['informes', 'diario', 'grandes', desde, hasta, cuantos],
    queryFn: async () => {
      const { data: filas } = await aqlQuery(
        q('transactions')
          .filter({
            $and: [{ date: { $gte: desde } }, { date: { $lte: hasta } }],
            amount: { $lt: 0 },
            'category.is_income': false,
            ...SIN_TRASPASOS,
          })
          .select(['id', 'date', 'amount', 'payee', 'category', 'notes'])
          .orderBy([{ amount: 'asc' }, { date: 'desc' }])
          .limit(cuantos),
      );
      return filas as GastoGrande[];
    },
    staleTime: CADUCIDAD_MS,
  });
  return data ?? [];
}

/**
 * Saldo de todas las cuentas (las de fuera del presupuesto también) al
 * final de cada fecha pedida. Para «patrimonio desde agosto».
 */
export function usePatrimonioEn(
  fechas: readonly string[],
): Array<IntegerAmount | undefined> {
  const clave = fechas.join('|');
  const { data } = useQuery({
    queryKey: ['informes', 'diario', 'patrimonio', clave],
    queryFn: async () =>
      Promise.all(
        fechas.map(async fecha => {
          const { data: suma } = await aqlQuery(
            q('transactions')
              .filter({ date: { $lte: fecha }, 'account.closed': false })
              .calculate({ $sum: '$amount' }),
          );
          return (suma as number | null) ?? 0;
        }),
      ),
    staleTime: CADUCIDAD_MS,
  });
  return fechas.map((_, i) => data?.[i]);
}

/**
 * «De nómina a nómina» en los informes (pref sincronizada
 * `diario-corte`): vacío = mes natural; un número 1..31 o `ultimo` = el día
 * en que empieza cada periodo.
 */
export function useCorteDiario(): [
  DiaCorte | null,
  (corte: DiaCorte | null) => void,
] {
  const [raw, setRaw] = useSyncedPref('diario-corte');
  const corte = leerCorte(raw);
  return [corte, nuevo => setRaw(nuevo == null ? '' : String(nuevo))];
}

export function leerCorte(raw: string | null | undefined): DiaCorte | null {
  if (raw === 'ultimo') {
    return 'ultimo';
  }
  const n = Number(raw);
  return raw && Number.isInteger(n) && n >= 1 && n <= 31 ? n : null;
}
